import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteInfo, isComingSoon } from "@/hooks/useSiteInfo";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, X, Sparkles, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function ComingSoonRibbon() {
  const info = useSiteInfo();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!isComingSoon(info)) return null;
  if (dismissed) return null;
  if (user && user.role === "admin") return null;

  return (
    <>
      <div
        data-testid="coming-soon-ribbon"
        className="relative overflow-hidden text-[#F8FAFC]"
        style={{
          background:
            "linear-gradient(135deg, #05101F 0%, #0A1930 30%, #142C55 65%, #1E3A8A 100%)",
        }}
      >
        {/* Decorative blobs */}
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#3B5FB8] opacity-25 blur-3xl pointer-events-none animate-pulse-slow" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#94B8DA] opacity-15 blur-3xl pointer-events-none animate-pulse-slow" aria-hidden="true" />
        {/* Faint editorial rule top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#94B8DA]/50 to-transparent" aria-hidden="true" />

        {/* Close button — floating top-right */}
        <button
          onClick={() => setDismissed(true)}
          aria-label="Cerrar"
          className="absolute top-4 right-4 z-10 w-11 h-11 flex-shrink-0 flex items-center justify-center opacity-60 hover:opacity-100 border border-[#F8FAFC]/25 hover:border-[#F8FAFC]/60 transition-opacity"
          data-testid="coming-soon-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20 lg:py-24 text-center">
          {/* Pulsing badge */}
          <div className="inline-flex items-center gap-2 relative fade-up">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#94B8DA] opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#94B8DA]"></span>
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#94B8DA] font-semibold">
              Lanzamiento en preparación
            </span>
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#94B8DA] opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#94B8DA]"></span>
            </span>
          </div>

          {/* Massive editorial headline */}
          <h2 className="mt-6 font-serif text-6xl sm:text-6xl lg:text-8xl font-semibold leading-[0.98] tracking-tight fade-up-delay-1">
            Muy <em className="not-italic italic text-[#94B8DA]">pronto</em>.
          </h2>
          <p className="mt-5 max-w-2xl mx-auto font-serif text-lg sm:text-2xl lg:text-3xl leading-snug text-[#F8FAFC] fade-up-delay-2 px-2">
            Empezaremos muy pronto compartiendo piso y seguiremos creciendo como comunidad.
          </p>

          {/* Divider ornament */}
          <div className="mt-8 flex items-center justify-center gap-3 fade-up-delay-2" aria-hidden="true">
            <span className="h-px w-16 bg-[#94B8DA]/40" />
            <Sparkles className="w-4 h-4 text-[#94B8DA]" />
            <span className="h-px w-16 bg-[#94B8DA]/40" />
          </div>

          <p className="mt-6 text-sm sm:text-base text-[#CBD5E1] max-w-xl mx-auto fade-up-delay-3">
            Ahora estás viendo una demostración — todavía no se pueden publicar anuncios. Serás de los primeros en enterarte del día del lanzamiento.
          </p>

          {/* Empresa CTA block */}
          <div className="mt-10 pt-8 border-t border-[#94B8DA]/25 max-w-2xl mx-auto fade-up-delay-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#94B8DA] font-semibold mb-3">
              Para empresas y profesionales
            </p>
            <p className="font-serif italic text-lg sm:text-xl text-[#F8FAFC] leading-snug">
              ¿Quieres reservar tu espacio antes del lanzamiento?
            </p>
            <p className="text-sm text-[#CBD5E1] mt-2 max-w-lg mx-auto">
              Ponte en contacto y te ofreceremos una propuesta a medida. Sin perder el sentido de compartir.
            </p>
            <button
              onClick={() => setOpen(true)}
              data-testid="ribbon-join-waitlist"
              className="mt-5 inline-flex items-center gap-2 bg-[#F8FAFC] text-[#1E3A8A] px-5 sm:px-6 h-12 font-semibold hover:bg-[#94B8DA] hover:text-[#0A1930] transition-colors shadow-lg shadow-[#0A1930]/40 text-sm sm:text-base"
            >
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Contactar como empresa o profesional</span>
              <span className="sm:hidden">Contactar como empresa</span>
            </button>
          </div>
        </div>
        {/* Bottom fade to next section */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#94B8DA]/40 to-transparent" aria-hidden="true" />
      </div>
      <WaitlistDialog open={open} onOpenChange={setOpen} contactEmail={info?.contact_email} />
    </>
  );
}

export function WaitlistDialog({ open, onOpenChange, contactEmail = "contacto@buscocompartir.com" }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [sector, setSector] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", {
        email,
        name: company || null,
        kind: "empresa",
        notes: [sector ? `Sección: ${sector}` : "", notes].filter(Boolean).join(" · ") || null,
      });
      setDone(true);
      if (data.already) {
        toast.info("Ya estabas registrado. ¡Nos ponemos en contacto pronto!");
      } else {
        toast.success("Recibido. Te contactaremos con propuestas.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(false);
    setEmail("");
    setCompany("");
    setSector("");
    setNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="max-w-lg rounded-none border border-[#E2E8F0] bg-[#F8FAFC] p-8"
      >
        <DialogHeader className="text-left space-y-1">
          <p className="eyebrow flex items-center gap-1"><Briefcase className="w-3 h-3" /> Empresas y profesionales</p>
          <DialogTitle className="font-serif text-3xl font-semibold text-[#1A1A1A]">
            Anúnciate con nosotros.
          </DialogTitle>
          <DialogDescription className="text-[#475569] text-sm leading-relaxed">
            Si eres empresa o profesional relacionado con alguna de nuestras secciones (pisos, viajes,
            idiomas, actividades, ideas, clasificados, profesión o descuentos), déjanos tus datos y te
            ofreceremos propuestas de anuncio para llegar a nuestra comunidad.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="mt-4 space-y-4">
            <p className="font-serif text-2xl text-[#1A1A1A]">Gracias. Te tenemos apuntad@.</p>
            <p className="text-sm text-[#475569]">
              También puedes escribirnos directamente a{" "}
              <a href={`mailto:${contactEmail}`} className="text-[#3B5FB8] font-semibold link-underline">
                {contactEmail}
              </a>
            </p>
            <Button onClick={close} className="w-full rounded-none h-11 bg-[#1A1A1A] text-[#F8FAFC]">Cerrar</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-2 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">Nombre o empresa</Label>
                <Input
                  data-testid="waitlist-name-input"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  className="rounded-none h-11 border-[#CBD5E1] bg-white"
                  placeholder="Estudio Ejemplo S.L."
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">Email</Label>
                <Input
                  data-testid="waitlist-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-none h-11 border-[#CBD5E1] bg-white"
                  placeholder="tu@empresa.com"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">Sección de interés</Label>
              <select
                data-testid="waitlist-sector-select"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                required
                className="w-full rounded-none h-11 border border-[#CBD5E1] bg-white px-3 text-sm font-medium"
              >
                <option value="">Elige una sección...</option>
                <option value="Viajes">Viajes</option>
                <option value="Pisos">Pisos</option>
                <option value="Idiomas">Idiomas</option>
                <option value="Negocios">Negocios</option>
                <option value="Empleo">Empleo</option>
                <option value="Servicios">Servicios</option>
                <option value="Objetos">Objetos</option>
                <option value="Eventos">Eventos</option>
                <option value="Deporte">Deporte</option>
                <option value="Varias">Varias secciones</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">Cuéntanos brevemente (opcional)</Label>
              <Textarea
                data-testid="waitlist-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="rounded-none border-[#CBD5E1] bg-white"
                placeholder="Qué servicio o producto quieres anunciar y a quién va dirigido."
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="waitlist-submit"
              className="w-full rounded-none h-12 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] font-medium"
            >
              {busy ? "Enviando..." : "Enviar mi propuesta"}
            </Button>
            <p className="text-[11px] text-[#64748B] text-center leading-relaxed">
              Datos gestionados conforme al RGPD. Sólo los usaremos para responderte con propuestas de anuncio.
              También puedes escribirnos a{" "}
              <a href={`mailto:${contactEmail}`} className="link-underline text-[#3B5FB8] font-semibold">
                {contactEmail}
              </a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
